import React, { useState, useEffect } from "react";
import { fetchLeads, deleteLead } from "../api/leadApi";
import { toast } from "react-hot-toast";

export const LeadsManagement = () => {
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [detailLead, setDetailLead] = useState(null);

    const loadLeads = async () => {
        setLoading(true);
        try {
            const data = await fetchLeads();
            setLeads(Array.isArray(data) ? data : []);
        } catch (error) {
            toast.error("Failed to load leads");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadLeads();
    }, []);

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this lead?")) return;
        try {
            await deleteLead(id);
            toast.success("Lead deleted");
            setDetailLead(null);
            loadLeads();
        } catch (error) {
            toast.error("Failed to delete lead");
        }
    };

    const filtered = leads.filter((l) => {
        const q = search.toLowerCase();
        return (
            (l.firstName || "").toLowerCase().includes(q) ||
            (l.lastName || "").toLowerCase().includes(q) ||
            (l.email || "").toLowerCase().includes(q) ||
            (l.cellPhone || "").includes(q)
        );
    });

    const formatSubject = (subject) => {
        if (!subject) return "—";
        const arr = Array.isArray(subject) ? subject : [subject];
        return arr.join(", ");
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center p-8">
                <i className="pi pi-spin pi-spinner text-red-700" style={{ fontSize: "2rem" }}></i>
            </div>
        );
    }

    return (
        <div className="p-4" style={{ fontFamily: "Tahoma, sans-serif" }}>
            <h2 className="text-xl font-bold mb-4">Leads</h2>

            <div className="mb-4">
                <input
                    type="text"
                    placeholder="Search leads..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="p-2 border rounded w-full max-w-xs"
                />
            </div>

            <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-gray-300 text-sm">
                    <thead>
                        <tr className="bg-gray-100">
                            <th className="border border-gray-300 p-2 text-left">First Name</th>
                            <th className="border border-gray-300 p-2 text-left">Last Name</th>
                            <th className="border border-gray-300 p-2 text-left">Phone Number</th>
                            <th className="border border-gray-300 p-2 text-left">Email Address</th>
                            <th className="border border-gray-300 p-2 text-left">Request</th>
                            <th className="border border-gray-300 p-2 text-left">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.length === 0 && (
                            <tr>
                                <td colSpan={6} className="text-center p-4 text-gray-500">
                                    No leads found
                                </td>
                            </tr>
                        )}
                        {filtered.map((lead) => (
                            <tr key={lead.id} className="hover:bg-gray-50">
                                <td className="border border-gray-300 p-2">
                                    <button
                                        className="text-blue-600 hover:underline cursor-pointer bg-transparent border-none font-medium"
                                        onClick={() => setDetailLead(lead)}
                                    >
                                        {lead.firstName}
                                    </button>
                                </td>
                                <td className="border border-gray-300 p-2">
                                    <button
                                        className="text-blue-600 hover:underline cursor-pointer bg-transparent border-none font-medium"
                                        onClick={() => setDetailLead(lead)}
                                    >
                                        {lead.lastName}
                                    </button>
                                </td>
                                <td className="border border-gray-300 p-2">{lead.cellPhone || "—"}</td>
                                <td className="border border-gray-300 p-2">{lead.email || "—"}</td>
                                <td className="border border-gray-300 p-2" style={{ maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    {formatSubject(lead.subject)}
                                </td>
                                <td className="border border-gray-300 p-2">
                                    <button
                                        className="text-red-600 hover:underline cursor-pointer bg-transparent border-none text-sm"
                                        onClick={() => handleDelete(lead.id)}
                                    >
                                        Delete
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Lead Detail Popup */}
            {detailLead && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center"
                    onClick={() => setDetailLead(null)}
                >
                    <div
                        className="bg-white rounded-lg shadow-lg p-6 w-full max-w-lg max-h-[80vh] overflow-y-auto"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h3 className="text-lg font-bold mb-4">Lead Details</h3>
                        <div className="space-y-2 text-sm">
                            <DetailRow label="First Name" value={detailLead.firstName} />
                            <DetailRow label="Last Name" value={detailLead.lastName} />
                            <DetailRow label="Name Suffix" value={detailLead.nameSuffix} />
                            <DetailRow label="Preferred Title" value={detailLead.preferredTitle} />
                            {detailLead.preferredTitleOtherReason && (
                                <DetailRow
                                    label="Preferred Title Explain Other reason"
                                    value={detailLead.preferredTitleOtherReason}
                                />
                            )}
                            <DetailRow label="Cell Phone" value={detailLead.cellPhone} />
                            <DetailRow label="Phone number country name" value={detailLead.phoneCountryCode} />
                            <DetailRow label="Email" value={detailLead.email} />
                            <DetailRow label="Subject" value={formatSubject(detailLead.subject)} />
                            <DetailRow
                                label="Location"
                                value={
                                    Array.isArray(detailLead.location)
                                        ? detailLead.location.join(", ")
                                        : detailLead.location || "—"
                                }
                            />
                            <DetailRow label="How Did You Find Us" value={detailLead.howDidYouFindUs} />
                            <DetailRow label="Additional Info" value={detailLead.additionalInfo} />
                            <DetailRow
                                label="Submitted At"
                                value={
                                    detailLead.createdAt
                                        ? new Date(detailLead.createdAt).toLocaleString()
                                        : "—"
                                }
                            />
                        </div>
                        <div className="flex gap-2 mt-4">
                            <button
                                onClick={() => setDetailLead(null)}
                                className="px-4 py-2 border rounded hover:bg-gray-100"
                            >
                                Close
                            </button>
                            <button
                                onClick={() => handleDelete(detailLead.id)}
                                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                            >
                                Delete Lead
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

function DetailRow({ label, value }) {
    return (
        <div className="flex gap-2">
            <strong style={{ minWidth: "140px", color: "#6b7280" }}>{label}:</strong>
            <span>{value || "—"}</span>
        </div>
    );
}
